import { useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, Circle, CircleMarker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import LocationButton from "../components/LocationButton";
import "../App.css";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Custom POI icon with label
const createPOIIcon = (name) => {
  return L.divIcon({
    className: 'custom-poi-marker-public',
    html: `
      <div style="display: flex; flex-direction: column; align-items: center;">
        <div style="background-color: #ff4444; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); margin-bottom: 2px;"></div>
        <div style="background-color: rgba(255,255,255,0.95); padding: 2px 6px; border-radius: 3px; font-size: 11px; font-weight: 600; color: #333; white-space: nowrap; box-shadow: 0 1px 4px rgba(0,0,0,0.2);">${name}</div>
      </div>
    `,
    iconSize: [100, 40],
    iconAnchor: [50, 8],
  });
};

// Audio skip duration in seconds
const SKIP_DURATION = 10;

// Component to fit map bounds
function FitBounds({ route, pois }) {
  const map = useMap();
  
  useEffect(() => {
    const points = [];
    
    // Add route points
    if (route && route.length > 0) {
      route.forEach(p => points.push([p.lat, p.lon]));
    }
    
    // Add POI points
    if (pois && pois.length > 0) {
      pois.forEach(poi => points.push([poi.lat, poi.lon]));
    }
    
    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [route, pois, map]);
  
  return null;
}

function PublicTourView() {
  const { shareId } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [showRoute, setShowRoute] = useState(true);
  const [showPOIs, setShowPOIs] = useState(true);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const isSeekingRef = useRef(false);
  
  // Check if Waze button should be shown based on URL parameter
  const showWaze = new URLSearchParams(window.location.search).get('waze') === 'true';

  useEffect(() => {
    fetchTour();
  }, [shareId]);

  const fetchTour = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${getApiUrl()}/api/public/tours/${shareId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Tour not found or not published');
        }
        throw new Error('Failed to load tour');
      }
      
      const data = await response.json();
      setTour(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getApiUrl = () => {
    if (typeof window !== 'undefined' && window.APP_CONFIG) {
      return window.APP_CONFIG.apiUrl;
    }
    return 'http://localhost:8080';
  };

  const openInGoogleMaps = (lat, lon, name) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}&query_place_id=${encodeURIComponent(name)}`;
    window.open(url, '_blank');
  };

  const openInWaze = (lat, lon) => {
    const url = `https://waze.com/ul?ll=${lat},${lon}&navigate=yes`;
    window.open(url, '_blank');
  };

  const resetAudioState = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingAudio(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    
    // Clear media session
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null;
    }
  };

  const playAudio = (audioUrl, poiId, poiName) => {
    if (playingAudio === poiId) {
      // Stop audio (this is called when clicking Stop button)
      resetAudioState();
    } else {
      // Play new audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(`${getApiUrl()}${audioUrl}`);
      audioRef.current = audio;
      
      // Set up audio event listeners
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      
      audio.addEventListener('timeupdate', () => {
        if (!isSeekingRef.current) {
          setCurrentTime(audio.currentTime);
        }
      });
      
      audio.addEventListener('ended', () => {
        setPlayingAudio(null);
        setIsPlaying(false);
        setCurrentTime(0);
      });
      
      audio.play();
      setPlayingAudio(poiId);
      setIsPlaying(true);
      
      // Set media session metadata for lock screen controls
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: poiName,
          artist: tour.title,
          album: 'Tour Audio Guide',
        });
        
        // Set up media session action handlers
        navigator.mediaSession.setActionHandler('play', () => {
          audio.play();
          setIsPlaying(true);
        });
        
        navigator.mediaSession.setActionHandler('pause', () => {
          audio.pause();
          setIsPlaying(false);
        });
        
        navigator.mediaSession.setActionHandler('stop', () => {
          audio.pause();
          audio.currentTime = 0;
          setPlayingAudio(null);
          setIsPlaying(false);
          setCurrentTime(0);
        });
        
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          if (details.seekTime !== undefined) {
            audio.currentTime = details.seekTime;
            setCurrentTime(details.seekTime);
          }
        });
      }
    }
  };

  const handleStopAudio = () => {
    resetAudioState();
  };

  const handlePauseResume = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleSeekStart = () => {
    isSeekingRef.current = true;
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const newTime = parseFloat(e.target.value);
      setCurrentTime(newTime);
    }
  };

  const handleSeekEnd = (e) => {
    if (audioRef.current) {
      const newTime = parseFloat(e.target.value);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
    isSeekingRef.current = false;
  };

  const handleSkipForward = () => {
    if (audioRef.current) {
      const newTime = Math.min(audioRef.current.currentTime + SKIP_DURATION, duration);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleSkipBackward = () => {
    if (audioRef.current) {
      const newTime = Math.max(audioRef.current.currentTime - SKIP_DURATION, 0);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSliderBackground = () => {
    if (duration <= 0) return '#ddd';
    const percentage = (currentTime / duration) * 100;
    return `linear-gradient(to right, #4CAF50 0%, #4CAF50 ${percentage}%, #ddd ${percentage}%, #ddd 100%)`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading tour...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <h2>😕 {error}</h2>
        <p style={{ color: '#666' }}>This tour may have been unpublished or the link is incorrect.</p>
      </div>
    );
  }

  if (!tour) {
    return null;
  }

  const mapCenter = tour.route && tour.route.length > 0 
    ? [tour.route[Math.floor(tour.route.length / 2)].lat, tour.route[Math.floor(tour.route.length / 2)].lon]
    : [0, 0];

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex', 
      flexDirection: 'column', 
      background: '#f5f5f5',
      zIndex: 9999,
    }}>
      {/* Header */}
      <div style={{ 
        background: 'white', 
        borderBottom: '1px solid #e0e0e0', 
        padding: '16px 24px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 1001,
        flexShrink: 0,
      }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '600', color: '#333' }}>
          {tour.title}
        </h1>
      </div>

      {/* Main Content - Map + Sidebar */}
      <div style={{ flex: 1, position: 'relative', display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Sidebar for Desktop */}
        {selectedPOI && (
          <div 
            className="poi-sidebar"
            style={{
              width: '400px',
              maxWidth: '90vw',
              background: 'white',
              borderRight: '1px solid #e0e0e0',
              zIndex: 1100,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                {selectedPOI.name}
              </h2>
              <button
                onClick={() => setSelectedPOI(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  lineHeight: '1',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {selectedPOI.description && (
                <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                  {selectedPOI.description}
                </p>
              )}

              {selectedPOI.narration && (
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
                    Description
                  </h3>
                  <div style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '14px',
                    fontSize: '14px',
                    lineHeight: '1.7',
                    color: '#333',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {selectedPOI.narration}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
                {selectedPOI.audio_url && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    padding: '12px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                  }}>
                    {playingAudio !== selectedPOI.id ? (
                      <button
                        onClick={() => playAudio(selectedPOI.audio_url, selectedPOI.id, selectedPOI.name)}
                        style={{
                          background: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '14px 20px',
                          fontSize: '15px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        ▶ Play Audio
                      </button>
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handlePauseResume}
                            style={{
                              flex: 1,
                              background: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '14px 20px',
                              fontSize: '15px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                            }}
                          >
                            {isPlaying ? '⏸ Pause' : '▶ Resume'}
                          </button>
                          <button
                            onClick={handleStopAudio}
                            style={{
                              background: '#ff4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '14px 20px',
                              fontSize: '15px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                            }}
                          >
                            ⏹ Stop
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handleSkipBackward}
                            style={{
                              flex: 1,
                              background: '#607D8B',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px 12px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                            }}
                          >
                            -10s
                          </button>
                          <button
                            onClick={handleSkipForward}
                            style={{
                              flex: 1,
                              background: '#607D8B',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '10px 12px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                            }}
                          >
                            +10s
                          </button>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px', 
                          fontSize: '12px', 
                          color: '#666' 
                        }}>
                          <span style={{ minWidth: '38px' }}>{formatTime(currentTime)}</span>
                          <input
                            type="range"
                            min="0"
                            max={duration || 0}
                            value={currentTime}
                            onMouseDown={handleSeekStart}
                            onTouchStart={handleSeekStart}
                            onChange={handleSeek}
                            onMouseUp={handleSeekEnd}
                            onTouchEnd={handleSeekEnd}
                            style={{
                              flex: 1,
                              height: '6px',
                              borderRadius: '3px',
                              outline: 'none',
                              background: getSliderBackground(),
                              WebkitAppearance: 'none',
                              appearance: 'none',
                              cursor: 'pointer',
                            }}
                          />
                          <span style={{ minWidth: '38px', textAlign: 'right' }}>{formatTime(duration)}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <button
                  onClick={() => openInGoogleMaps(selectedPOI.lat, selectedPOI.lon, selectedPOI.name)}
                  style={{
                    background: '#4285f4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '14px 20px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  📍 Open in Google Maps
                </button>
                {showWaze && (
                  <button
                    onClick={() => openInWaze(selectedPOI.lat, selectedPOI.lon)}
                    style={{
                      background: '#33ccff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '14px 20px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    🚗 Open in Waze
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Map Container */}
        <div style={{ flex: 1, position: 'relative' }}>
          {/* Layer Controls */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 999,
            background: 'white',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '13px', color: '#333' }}>
              Layers
            </div>
            {tour.route && tour.route.length > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showRoute}
                  onChange={(e) => setShowRoute(e.target.checked)}
                  style={{ marginRight: '6px' }}
                />
                <span style={{ fontSize: '13px' }}>Route</span>
              </label>
            )}
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showPOIs}
                onChange={(e) => setShowPOIs(e.target.checked)}
                style={{ marginRight: '6px' }}
              />
              <span style={{ fontSize: '13px' }}>Points of Interest</span>
            </label>
          </div>

          {/* Mobile Bottom Drawer */}
          {selectedPOI && (
            <div 
              className="poi-drawer-mobile"
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                top: drawerExpanded ? 0 : 'auto',
                maxHeight: drawerExpanded ? '100vh' : '70vh',
                height: drawerExpanded ? '100vh' : 'auto',
                background: 'white',
                borderTopLeftRadius: drawerExpanded ? '0' : '16px',
                borderTopRightRadius: drawerExpanded ? '0' : '16px',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
                zIndex: 1200,
                display: 'none',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
              }}
            >
              {/* Drag handle */}
              <div 
                onClick={() => setDrawerExpanded(!drawerExpanded)}
                style={{
                  padding: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  borderBottom: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <div style={{
                  width: '40px',
                  height: '4px',
                  background: '#d0d0d0',
                  borderRadius: '2px',
                }}></div>
              </div>

              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                flexShrink: 0,
              }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
                  {selectedPOI.name}
                </h2>
                <button
                  onClick={() => {
                    setSelectedPOI(null);
                    setDrawerExpanded(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    padding: '0',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666',
                    lineHeight: '1',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden',
                padding: '16px 20px',
                WebkitOverflowScrolling: 'touch',
                minHeight: 0,
              }}>
                {selectedPOI.description && (
                  <p style={{ margin: '0 0 14px 0', color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                    {selectedPOI.description}
                  </p>
                )}

                {selectedPOI.narration && (
                  <div style={{ marginBottom: '14px' }}>
                    <h3 style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '6px' }}>
                      Description
                    </h3>
                    <div style={{
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      color: '#333',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {selectedPOI.narration}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '16px' }}>
                  {selectedPOI.audio_url && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      padding: '10px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                    }}>
                      {playingAudio !== selectedPOI.id ? (
                        <button
                          onClick={() => playAudio(selectedPOI.audio_url, selectedPOI.id, selectedPOI.name)}
                          style={{
                            background: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                          }}
                        >
                          ▶ Play Audio
                        </button>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={handlePauseResume}
                              style={{
                                flex: 1,
                                background: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                              }}
                            >
                              {isPlaying ? '⏸ Pause' : '▶ Resume'}
                            </button>
                            <button
                              onClick={handleStopAudio}
                              style={{
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                              }}
                            >
                              ⏹ Stop
                            </button>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={handleSkipBackward}
                              style={{
                                flex: 1,
                                background: '#607D8B',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                              }}
                            >
                              -10s
                            </button>
                            <button
                              onClick={handleSkipForward}
                              style={{
                                flex: 1,
                                background: '#607D8B',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                              }}
                            >
                              +10s
                            </button>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            fontSize: '11px', 
                            color: '#666' 
                          }}>
                            <span style={{ minWidth: '35px' }}>{formatTime(currentTime)}</span>
                            <input
                              type="range"
                              min="0"
                              max={duration || 0}
                              value={currentTime}
                              onMouseDown={handleSeekStart}
                              onTouchStart={handleSeekStart}
                              onChange={handleSeek}
                              onMouseUp={handleSeekEnd}
                              onTouchEnd={handleSeekEnd}
                              style={{
                                flex: 1,
                                height: '6px',
                                borderRadius: '3px',
                                outline: 'none',
                                background: getSliderBackground(),
                                WebkitAppearance: 'none',
                                appearance: 'none',
                                cursor: 'pointer',
                              }}
                            />
                            <span style={{ minWidth: '35px', textAlign: 'right' }}>{formatTime(duration)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => openInGoogleMaps(selectedPOI.lat, selectedPOI.lon, selectedPOI.name)}
                    style={{
                      background: '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    📍 Google Maps
                  </button>
                  {showWaze && (
                    <button
                      onClick={() => openInWaze(selectedPOI.lat, selectedPOI.lon)}
                      style={{
                        background: '#33ccff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                      }}
                    >
                      🚗 Waze
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Map */}
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ width: '100%', height: '100%' }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            />
            
            <FitBounds route={tour.route} pois={tour.pois} />
            <LocationButton />

            {/* Route polyline */}
            {showRoute && tour.route && tour.route.length > 0 && (
              <Polyline
                positions={tour.route.map(p => [p.lat, p.lon])}
                color="#2196F3"
                weight={4}
                opacity={0.7}
              />
            )}

            {/* POI markers */}
            {showPOIs && tour.pois && tour.pois.map((poi) => (
              <Marker
                key={poi.id}
                position={[poi.lat, poi.lon]}
                icon={createPOIIcon(poi.name)}
                eventHandlers={{
                  click: () => setSelectedPOI(poi),
                }}
              />
            ))}
          </MapContainer>
        </div>
      </div>

      {/* CSS for responsive layout */}
      <style>{`
        @media (max-width: 768px) {
          .poi-sidebar {
            display: none !important;
          }
          .poi-drawer-mobile {
            display: flex !important;
          }
        }
        @media (min-width: 769px) {
          .poi-drawer-mobile {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default PublicTourView;
